import { Suspense, useDeferredValue, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import {
    BlockStack,
    Box,
    InlineStack,
    PageHeader,
    SegmentedControl,
    TextField,
    Typography,
    type SegmentedOption,
} from '@pv-frontend/ui-components-web';
import { auditLogQuery } from '../relay/audit-log-query';
import type { auditLogQuery as AuditLogQuery } from '../relay/__generated__/auditLogQuery.graphql';
import { AuditTable, type AuditEntryView } from './audit-table';
import { LoadingContent } from './loading-content';

/**
 * Flag filter values: every `FeatureKey`, plus `ALL` for the unscoped global log.
 * Short labels keep the segmented track scannable; the values are the enum keys
 * the query expects.
 */
type FlagFilter = 'ALL' | 'REALTIME_PRESENCE' | 'VOICE' | 'REMOTE_FETCH_IMPORT' | 'ADDITIONAL_LISTS' | 'BIOMETRIC_LOCK';

const FLAG_OPTIONS: readonly SegmentedOption<FlagFilter>[] = [
    { value: 'ALL', label: 'All' },
    { value: 'REALTIME_PRESENCE', label: 'Presence' },
    { value: 'VOICE', label: 'Voice' },
    { value: 'REMOTE_FETCH_IMPORT', label: 'Import' },
    { value: 'ADDITIONAL_LISTS', label: 'Lists' },
    { value: 'BIOMETRIC_LOCK', label: 'Biometric' },
];

/**
 * Toggle audit & history (slice 3, pv-frontend#29). Reads the `auditLog` trail and
 * renders it as an accessible data table — action, old→new, operator, timestamp.
 * The flag filter scopes the read server-side, giving a per-flag history; picking
 * `All` shows the global log. Vault / operator refine the visible rows client-side
 * so those filters are instant and forgiving of partial input.
 *
 * The flag filter re-runs the query, so it sits behind its own Suspense boundary
 * (deferred, so the previous rows stay on screen while the new read resolves); the
 * text filters never re-query, so the fields keep focus as you type.
 */
export function AuditScreen() {
    const [flag, setFlag] = useState<FlagFilter>('ALL');
    const [vault, setVault] = useState('');
    const [operator, setOperator] = useState('');
    const deferredFlag = useDeferredValue(flag);

    return (
        <Box padding="600" maxWidth={1080}>
            <BlockStack gap="500">
                <PageHeader
                    title="Audit log"
                    description="Every feature-toggle change from the Admin API, newest first — who changed what, old→new, and when. Read-only."
                />

                <BlockStack gap="300">
                    <BlockStack gap="150">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Flag
                        </Typography>
                        <SegmentedControl
                            accessibilityLabel="Filter the audit log by flag"
                            options={FLAG_OPTIONS}
                            value={flag}
                            onChange={setFlag}
                        />
                    </BlockStack>

                    <InlineStack gap="300" wrap>
                        <Box flex={1} minWidth={220}>
                            <TextField
                                label="Vault"
                                value={vault}
                                onChange={setVault}
                                placeholder="Filter by vault…"
                                type="search"
                                leadingIcon="search"
                            />
                        </Box>
                        <Box flex={1} minWidth={220}>
                            <TextField
                                label="Operator"
                                value={operator}
                                onChange={setOperator}
                                placeholder="Filter by operator…"
                                type="search"
                                leadingIcon="search"
                            />
                        </Box>
                    </InlineStack>
                </BlockStack>

                <Suspense fallback={<LoadingContent />}>
                    <AuditResults flag={deferredFlag} vault={vault} operator={operator} />
                </Suspense>
            </BlockStack>
        </Box>
    );
}

/**
 * Fetches the audit trail for the (server-side) flag filter and hands the rows to
 * the pure table, which applies the vault / operator refinement. Kept separate so
 * only this subtree suspends when the flag changes.
 */
function AuditResults({
    flag,
    vault,
    operator,
}: {
    flag: FlagFilter;
    vault: string;
    operator: string;
}) {
    const data = useLazyLoadQuery<AuditLogQuery>(
        auditLogQuery,
        { flag: flag === 'ALL' ? null : flag },
        { fetchPolicy: 'store-or-network' },
    );

    const entries: AuditEntryView[] = data.auditLog.map((entry) => ({
        id: entry.id,
        action: entry.action,
        target: entry.target,
        oldValue: entry.oldValue ?? null,
        newValue: entry.newValue ?? null,
        operator: entry.caller,
        whenUtc: entry.whenUtc,
    }));

    return <AuditTable entries={entries} vault={vault} operator={operator} />;
}
